import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [track]
 * @property {HTMLButtonElement} [btnPrev]
 * @property {HTMLButtonElement} [btnNext]
 *
 * @extends {Component<Refs>}
 */
class CustomJeansBanner extends Component {
  #index = 0;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  prev() {
    this.#go(this.#index - 1);
  }

  next() {
    this.#go(this.#index + 1);
  }

  #go(index) {
    const maxIndex = this.#getMaxIndex();
    this.#index = Math.max(0, Math.min(index, maxIndex));
    this.#update();
  }

  #getVisibleSlides() {
    const value = parseInt(
      getComputedStyle(this).getPropertyValue('--plp-jeans-visible-slides').trim(),
      10,
    );

    return Number.isNaN(value) || value <= 0 ? 1 : value;
  }

  #getMaxIndex() {
    const slides = this.refs.slides ?? [];
    return Math.max(0, slides.length - this.#getVisibleSlides());
  }

  #update() {
    const slides = this.refs.slides ?? [];
    const track = this.refs.track;
    const maxIndex = this.#getMaxIndex();
    const centered = slides.length <= this.#getVisibleSlides();

    if (!track || !slides.length) {
      if (this.refs.btnPrev) this.refs.btnPrev.disabled = true;
      if (this.refs.btnNext) this.refs.btnNext.disabled = true;
      return;
    }

    this.#index = Math.min(this.#index, maxIndex);

    if (centered) {
      this.setAttribute('data-centered', '');
      track.style.setProperty('--plp-jeans-offset', '0px');
    } else {
      this.removeAttribute('data-centered');
      track.style.setProperty('--plp-jeans-offset', `${-slides[this.#index].offsetLeft}px`);
    }

    if (this.refs.btnPrev) this.refs.btnPrev.disabled = centered || this.#index <= 0;
    if (this.refs.btnNext) this.refs.btnNext.disabled = centered || this.#index >= maxIndex;
  }
}

if (!customElements.get('custom-jeans-banner')) {
  customElements.define('custom-jeans-banner', CustomJeansBanner);
}
